import { Injectable, Signal, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { AccountInfo } from '@azure/msal-browser';
import { Observable, catchError, from, map, of, tap } from 'rxjs';
import { IEmployee } from '../../classes/IEmployee';
import { EmployeeService } from '../employee/employee.service';
import { getGidFromClaims, mergeEmployeeWithClaims } from './employee-claims';

@Injectable({
    providedIn: 'root'
})
export class CurrentEmployeeService {
    private readonly employeeService = inject(EmployeeService);
    private readonly router = inject(Router);
    private readonly currentEmployee = signal<IEmployee | null>(null);
    private loadedGid: string | null = null;

    public readonly user: Signal<IEmployee | null> = this.currentEmployee.asReadonly();

    public clear(): void {
        this.loadedGid = null;
        this.currentEmployee.set(null);
    }

    public load(account: AccountInfo): Observable<boolean> {
        const gid = getGidFromClaims(account.idTokenClaims);

        if (!gid) {
            console.error('Authenticated account is missing a supported GID claim.');
            return this.resetAndRedirectToNoAccess();
        }

        if (this.loadedGid === gid && this.currentEmployee()) {
            return of(true);
        }

        return this.employeeService.getEmployee(gid).pipe(
            tap((employee) => {
                this.currentEmployee.set(mergeEmployeeWithClaims(employee, account, gid));
                this.loadedGid = gid;
            }),
            map(() => true),
            catchError((error) => {
                console.error('Failed to load user data', error);
                return this.resetAndRedirectToNoAccess();
            })
        );
    }

    private resetAndRedirectToNoAccess(): Observable<boolean> {
        this.clear();
        return from(this.router.navigateByUrl('/no-access')).pipe(map(() => false));
    }
}
